/**
 * ClientSessionBanner — app-wide strip shown while a staffer holds a REAL
 * "signed in as client" session (POST /api/clients/:id/sign-in-as). Sits next
 * to ViewAsBanner but means the opposite thing: this is NOT a read-only lens.
 * The token itself is client-scoped (`manager`), the server treats every
 * request as that client's user, and writes land for real — which is exactly
 * the fidelity the pre-ship QA pass needs, and exactly why the strip must be
 * impossible to miss.
 */
import { useAuth } from '../auth/AuthContext'

export default function ClientSessionBanner() {
  const { clientSession, exitClientSession, user } = useAuth()
  if (!clientSession) return null

  return (
    <div
      role="status"
      style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        margin: '0 0 18px', padding: '10px 16px',
        // Pinned to the top of the scrolling <main> so it can't scroll away:
        // the way back must be one click from anywhere on the page.
        position: 'sticky', top: 0, zIndex: 50,
        borderRadius: 'var(--radius-md, 10px)',
        background: 'linear-gradient(90deg, color-mix(in srgb, var(--parkify-accent) 12%, transparent), rgba(21,128,61,0.12)), var(--color-white, #fff)',
        border: '1px solid color-mix(in srgb, var(--parkify-accent) 35%, transparent)',
        fontSize: 13, lineHeight: 1.4, color: '#231F20',
      }}
    >
      <KeyIcon />
      <span style={{ fontWeight: 700 }}>
        Signed in as {clientSession.clientName}
      </span>
      <span style={{ color: 'var(--color-gray-700)' }}>
        Manager · real client session
      </span>
      <span style={{
        fontSize: 11, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase',
        padding: '2px 8px', borderRadius: 999,
        background: 'rgba(21,128,61,0.15)', color: '#15803d',
      }}>
        Writes are live
      </span>
      <span style={{ flex: 1 }} />
      {user && (
        <span style={{ fontSize: 11, color: 'var(--color-gray-500)' }}>
          You are {user.name?.split(' ')[0] ?? user.email}
        </span>
      )}
      <button
        onClick={exitClientSession}
        style={{
          padding: '6px 14px', borderRadius: 'var(--radius-btn)', cursor: 'pointer', fontFamily: 'inherit',
          fontSize: 12, fontWeight: 700, color: 'var(--color-white)',
          background: 'var(--parkify-accent)', border: 'none',
        }}
      >
        Exit client session
      </button>
    </div>
  )
}

function KeyIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--parkify-accent)"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <circle cx="8" cy="15" r="4" />
      <path d="M10.85 12.15 19 4" />
      <path d="M18 5l2 2" />
      <path d="M15 8l2 2" />
    </svg>
  )
}
